// src/components/landing/Hero.jsx
import React from 'react';
import { Link } from 'react-router-dom';

const Hero = () => {
  return (
    <section className="relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="text-center">
          {/* Tagline */}
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm font-medium mb-6">
            🚀 AI-Powered Note Taking
          </div>
          
          {/* Main Headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Transform Your Notes into
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Intelligence
            </span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            Summarize, generate flashcards, and search through your notes with AI. 
            The smart note-taking platform for students, professionals, and lifelong learners.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Free Trial
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <Link
              to="/demo"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Watch Demo
              <svg className="ml-2 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">10,000+</div>
              <div className="text-gray-600 dark:text-gray-400">Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">1M+</div>
              <div className="text-gray-600 dark:text-gray-400">Notes Processed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">99%</div>
              <div className="text-gray-600 dark:text-gray-400">Satisfaction Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">24/7</div>
              <div className="text-gray-600 dark:text-gray-400">AI Support</div>
            </div>
          </div>
        </div>
        
        {/* Hero Image/Illustration */}
        <div className="mt-20 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-900 to-transparent z-10" />
          <div className="relative z-0 bg-gray-100 dark:bg-gray-800 rounded-2xl p-8 shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Mock Dashboard Preview */}
              <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <div className="ml-4 text-sm text-gray-500 dark:text-gray-400">Dashboard Preview</div>
                </div>
                <div className="space-y-4">
                  <div className="h-4 bg-gradient-to-r from-blue-200 to-purple-200 dark:from-blue-900/30 dark:to-purple-900/30 rounded"></div>
                  <div className="h-4 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-800/20 dark:to-purple-800/20 rounded w-3/4"></div>
                  <div className="grid grid-cols-3 gap-4 mt-8">
                    <div className="h-20 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4">
                      <div className="h-4 bg-blue-200 dark:bg-blue-700 rounded mb-2"></div>
                      <div className="h-3 bg-blue-100 dark:bg-blue-600 rounded w-1/2"></div>
                    </div>
                    <div className="h-20 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4">
                      <div className="h-4 bg-purple-200 dark:bg-purple-700 rounded mb-2"></div>
                      <div className="h-3 bg-purple-100 dark:bg-purple-600 rounded w-1/2"></div>
                    </div>
                    <div className="h-20 bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/20 dark:to-pink-800/20 rounded-lg p-4">
                      <div className="h-4 bg-pink-200 dark:bg-pink-700 rounded mb-2"></div>
                      <div className="h-3 bg-pink-100 dark:bg-pink-600 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* AI Features Preview */}
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl p-6 text-white">
                  <div className="text-2xl mb-2">🤖</div>
                  <h3 className="font-bold mb-2">AI Summarizer</h3>
                  <p className="text-sm opacity-90">Turn long notes into concise summaries</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-xl p-6 text-white">
                  <div className="text-2xl mb-2">🎴</div>
                  <h3 className="font-bold mb-2">Flashcard Generator</h3>
                  <p className="text-sm opacity-90">Auto-create study flashcards</p>
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 text-white">
                  <div className="text-2xl mb-2">🔍</div>
                  <h3 className="font-bold mb-2">Smart Search</h3>
                  <p className="text-sm opacity-90">Find anything in your notes instantly</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;